import { useBlocker } from "react-router";
import { Dispatch, SetStateAction, useEffect, useEffectEvent } from "react";

/**
 * Block internal navigation if any save guard is enabled.
 *
 * This functionality is only extracted for readability. Do NOT reuse this hook.
 */
function useInternalNavigationBlocker(
  saveGuardEnabledIds: Set<string>,
  setSaveGuardEnabledIds: Dispatch<SetStateAction<Set<string>>>,
  setLeaveHandler: (newLeaveHandler: (() => void) | null) => void,
  setStayHandler: (newStayHandler: (() => void) | null) => void
) {
  /**
   * Before navigation happens using React Router, and any save guard is enabled,
   * 'state' changes to "blocked". You then call 'proceed' or 'reset' to either
   * go through with the navigation of abort it.
   */
  const { state, proceed, reset } = useBlocker(saveGuardEnabledIds.size > 0);

  /**
   * When navigation is blocked, set both the 'leaveHandler' and 'stayHandler'
   * in order to show the save guard prompt.
   *
   * Set the 'leaveHandler' to
   *  - reset the 'leaveHandler' and 'stayHandler' in order to hide the save guard prompt
   *  - disable all save guards
   *  - proceed with the navigation
   *
   * Set the 'stayHandler' to
   *  - reset the 'leaveHandler' and 'stayHandler' in order to hide the save guard prompt
   *  - abort the navigation
   */
  const handleNavigationBlocked = useEffectEvent(() => {
    if (state === "blocked") {
      if (saveGuardEnabledIds.size > 0) {
        setLeaveHandler(() => {
          setLeaveHandler(null);
          setStayHandler(null);
          setSaveGuardEnabledIds(new Set());
          proceed();
        });
        setStayHandler(() => {
          setStayHandler(null);
          setLeaveHandler(null);
          reset();
        });
      } else {
        // This case should not be possible, but 'useBlocker' can incorrectly
        // block even though its boolean argument is false...
        proceed();
      }
    }
  });

  /**
   * Unfortunately, 'useBlocker' doesn't provide an 'onBlocked' callback, so we
   * have to catch the change in our own 'useEffect'.
   */
  useEffect(() => {
    if (state === "blocked") {
      handleNavigationBlocked();
    }
  }, [state]);
}

export default useInternalNavigationBlocker;
